/**
 * PrescreeningSummaryModal — export a compact, one-file PDF reference of
 * everything collected about a lead (Q&A across channels, medical profile,
 * eligibility determinations, records on file), grouped by study, for the CRC /
 * investigator to read at the screening visit inside CRIO.
 *
 * The recruiter checks which sections to include, then either downloads the PDF
 * or emails it to CRIO's document-intake inbox (the same pathway records use).
 * Every export is saved to the lead's Records (doc_type 'prescreening_summary'),
 * so past summaries are listed here for re-download. Always a PDF.
 */
import { useCallback, useEffect, useState } from 'react';
import { FileDown, Send, Loader2, Check, Download } from 'lucide-react';
import api from '../../../services/api';
import { useConfirm, Z } from '../ui';

interface Props {
  contactId: string;
  onClose: () => void;
}

type AspectKey = 'qa' | 'profile' | 'determinations' | 'records';

const ASPECTS: { key: AspectKey; label: string; desc: string }[] = [
  { key: 'qa', label: 'Prescreening Q&A', desc: 'Questions asked & answered across the web screener, recruiter prescreen, and questionnaires — grouped by study.' },
  { key: 'profile', label: 'Medical profile', desc: 'Conditions, medications, and labs built from records/forms/calls, with source badges.' },
  { key: 'determinations', label: 'Eligibility determinations', desc: 'Per-study AI eligibility review outcome + the screening verdict.' },
  { key: 'records', label: 'Medical records on file', desc: 'List of the records pulled for this lead (filename, faxed vs uploaded, date).' },
];

interface SummaryDoc {
  id: string;
  doc_type: string;
  filename: string;
  file_size?: number | null;
  created_at: string;
}

/** With responseType:'blob', axios delivers JSON error bodies as a Blob — read
 *  and parse it so the user sees the real message, not "[object Blob]". */
async function blobErrMsg(e: any, fallback: string): Promise<string> {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.detail ? `${parsed.error} — ${parsed.detail}` : (parsed.error || fallback);
    } catch { return fallback; }
  }
  const d = e?.response?.data;
  if (d?.error) return d.detail ? `${d.error} — ${d.detail}` : d.error;
  return e?.message || fallback;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameFromDisposition(disposition?: string, fallback = 'prescreening-summary.pdf'): string {
  const m = /filename="?([^"]+)"?/.exec(disposition || '');
  return m ? m[1] : fallback;
}

function relDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PrescreeningSummaryModal({ contactId, onClose }: Props) {
  const confirm = useConfirm();
  const [aspects, setAspects] = useState<Record<AspectKey, boolean>>({
    qa: true, profile: true, determinations: true, records: true,
  });
  const [busy, setBusy] = useState<'download' | 'crio' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentMsg, setSentMsg] = useState<string | null>(null);
  const [previous, setPrevious] = useState<SummaryDoc[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const selectedKeys = (Object.keys(aspects) as AspectKey[]).filter((k) => aspects[k]);

  const loadPrevious = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/documents`);
      const docs: SummaryDoc[] = (res.data?.documents || []).filter((d: SummaryDoc) => d.doc_type === 'prescreening_summary');
      setPrevious(docs);
    } catch { /* non-fatal — the list is a convenience */ }
  }, [contactId]);

  useEffect(() => { loadPrevious(); }, [loadPrevious]);

  const toggle = (key: AspectKey) => setAspects((a) => ({ ...a, [key]: !a[key] }));

  const download = async () => {
    setBusy('download');
    setError(null);
    setSentMsg(null);
    try {
      const res = await api.post(
        `/api/crm/contacts/${contactId}/prescreening-summary/pdf`,
        { aspects },
        { responseType: 'blob' },
      );
      triggerDownload(res.data, filenameFromDisposition(res.headers?.['content-disposition']));
      loadPrevious();
    } catch (e: any) {
      setError(await blobErrMsg(e, 'Could not build the summary'));
    } finally {
      setBusy(null);
    }
  };

  const sendToCrio = async () => {
    if (!(await confirm({
      title: 'Email this prescreening summary (contains PHI) to the CRIO document inbox?',
      confirmLabel: 'Send',
    }))) return;
    setBusy('crio');
    setError(null);
    setSentMsg(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/prescreening-summary/send-to-crio`, { aspects });
      setSentMsg(res.data?.overridden ? 'Sent to the test override inbox ✓' : 'Sent to CRIO ✓');
      loadPrevious();
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.detail ? `${d.error} — ${d.detail}` : (d?.error || e?.message || 'Could not send to CRIO'));
    } finally {
      setBusy(null);
    }
  };

  const redownload = async (doc: SummaryDoc) => {
    setDownloadingId(doc.id);
    try {
      const res = await api.get(`/api/crm/documents/${doc.id}/file`, { responseType: 'blob' });
      triggerDownload(res.data, doc.filename || 'prescreening-summary.pdf');
    } catch (e: any) {
      setError(await blobErrMsg(e, 'Could not open that summary'));
    } finally {
      setDownloadingId(null);
    }
  };

  const anyBusy = busy !== null;

  return (
    <div
      className={`fixed inset-0 ${Z.modalRaised} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-xl bg-white p-5 shadow-xl dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Export prescreening summary"
      >
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <FileDown className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Export prescreening summary
        </h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          A compact PDF for the screening visit — everything we&apos;ve collected, grouped by study. Choose what to include.
        </p>

        <div className="mt-4 space-y-2">
          {ASPECTS.map((a) => (
            <label
              key={a.key}
              className="flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/40 sm:min-h-0"
            >
              <input
                type="checkbox"
                checked={aspects[a.key]}
                onChange={() => toggle(a.key)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{a.label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{a.desc}</span>
              </span>
            </label>
          ))}
        </div>

        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Lead identity and the studies they&apos;re being considered for are always included. Every export is saved to the lead&apos;s Records.
        </p>

        {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {sentMsg && <p className="mt-3 inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400"><Check className="h-4 w-4" />{sentMsg}</p>}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Close
          </button>
          <button
            type="button"
            onClick={sendToCrio}
            disabled={anyBusy || selectedKeys.length === 0}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-primary-600 px-4 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
          >
            {busy === 'crio' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send to CRIO
          </button>
          <button
            type="button"
            onClick={download}
            disabled={anyBusy || selectedKeys.length === 0}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {busy === 'download' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Download PDF
          </button>
        </div>

        {previous.length > 0 && (
          <div className="mt-5 border-t border-gray-200 pt-3 dark:border-gray-700">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Previous summaries</h4>
            <ul className="mt-2 space-y-1">
              {previous.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm text-gray-700 dark:text-gray-200" title={d.filename}>
                    {d.filename} <span className="text-xs text-gray-400">· {relDate(d.created_at)}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => redownload(d)}
                    disabled={downloadingId === d.id}
                    aria-label={`Download ${d.filename}`}
                    className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-lg p-1 text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                  >
                    {downloadingId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
